import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { renderMarkdown } from '../../../common/components/markdown/markdown';
import TierChip from '../../../common/components/tier-chip/tier-chip';
import { formatDateTime } from '../../../core/utils/date';
import { Gate, useAuth } from '../../../global/contexts/auth/auth';
import { useAuthorNames, useRepositoryInfo, useTierLabel } from '../../../global/contexts/lookups/lookups';
import ProjectFilter from '../../projects/layouts/project-filter';
import KindBadge from '../../repositories/layouts/kind-badge';
import { useDecideRound, useReviewQueue } from '../contexts/reviews';
import { FINDING_LABEL, approvalBlocker } from '../utils/delivery';

import '../styles/queue.css';

// The deliveries waiting for a decision, and the decision.
//
// WHAT IS ON THE CARD IS WHAT DECIDING NEEDS, in the order somebody reads it:
//
//   1. which round, delivered by whom and when
//   2. the EXACT VERSION — base commit → delivered commit — which is what an
//      approval signs, and the repository and its main branch
//   3. the commits, with message, author and date
//   4. the evaluation per criterion (the AI evaluates; it does not decide)
//   5. the round's log, and what the agent reported while working
//   6. the previous returns
//
// THE DECISION CITES THE ROUND through `review-round/decision`. The route this
// screen used before is retired in Core and answers 410.
//
// WHAT CORE DOES NOT RECORD, and this card does not invent: the working branch
// of a delivery. The version is fixed by the two commits; the branch shown is
// the repository's main one, labelled as such.

export default function ReviewQueue() {
    const [params, setParams] = useSearchParams();
    const projectId = params.get('project') || '';
    // LA TAREA SEÑALADA desde el tablero (`?tarea=`). No filtra la cola: la
    // trae a la vista y la destaca, porque quien llegó mirando una entrega
    // también quiere ver cuántas más están esperando detrás.
    const focused = params.get('tarea') || '';

    const { cards, canReadEvents, loaded, total, isLoading, error } = useReviewQueue({ projectId });
    const { can, user } = useAuth();
    const names = useAuthorNames();
    const repositoryInfo = useRepositoryInfo();
    const tierLabel = useTierLabel();
    const canOpenTiers = can('tiers:read');

    // Traerla a la vista cuando llega, y solo entonces: la cola carga por
    // partes, así que el efecto espera a que la tarjeta exista.
    useEffect(() => {
        if (!focused) return;
        const card = document.getElementById(`entrega-${focused}`);
        card?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [focused, cards.length]);

    const setProject = (value) => {
        const target = new URLSearchParams(params);
        if (value) target.set('project', value);
        else target.delete('project');
        target.delete('repo');
        target.delete('tier');
        setParams(target);
    };

    if (error) {
        return (
            <p className="reviews__error">
                {error.message || 'No se pudo cargar la cola de revisión'}
            </p>
        );
    }

    return (
        <section className="reviews">
            <header className="reviews__header">
                <div className="reviews__heading">
                    <span className="reviews__hint">Compuerta</span>
                    <h2 className="reviews__title">Esperando revisión</h2>
                </div>
            </header>

            <div className="reviews__filters">
                <ProjectFilter value={projectId} onChange={setProject} />
            </div>

            {isLoading && <p className="reviews__empty">Cargando…</p>}

            {/* Señalaron una tarea que no está esperando: o ya se decidió, o
                quedó fuera del filtro de proyecto. Se dice, con la puerta a su
                ficha, en vez de dejar la cola como si nada hubiera pasado. */}
            {!isLoading && focused && !cards.some(card => String(card.task.id) === focused) && (
                <p className="reviews__missing">
                    La tarea <Link to={`/admin/tasks/${focused}`}>#{focused}</Link> no está
                    esperando revisión: su ronda ya se decidió, o queda fuera del proyecto
                    elegido.
                </p>
            )}

            {!isLoading && !cards.length && (
                <p className="reviews__empty">
                    Nada esperando revisión. Ninguna entrega quedó sin mirar.
                </p>
            )}

            <div className="reviews__list">
                {cards.map(card => (
                    <Delivery
                        key={card.task.id}
                        card={card}
                        focused={String(card.task.id) === focused}
                        canReadEvents={canReadEvents}
                        // THE GATE COMPARES THE ACTOR — the (person, token)
                        // pair. From the panel the reviewer is the session,
                        // (user, null), so the only delivery it refuses is one
                        // made by that same session.
                        isSameActor={card.reservation?.userId === user?.id && card.reservation?.tokenId == null}
                        names={names}
                        repositoryInfo={repositoryInfo}
                        tier={card.task.modality === 'one_off'
                            ? { short: 'Puntual', full: 'Tarea puntual: sin tier ni plan' }
                            : tierLabel(card.task.tier_id)}
                        tierTo={canOpenTiers && card.task.tier_id ? `/admin/tiers/${card.task.tier_id}` : null}
                    />
                ))}
            </div>

            <footer className="reviews__foot">
                <p className="reviews__note">
                    Aprobar una ronda aprueba <strong>la versión que esa ronda fijó</strong> —el
                    commit entregado—, no la tarea para siempre, y no significa integrada ni
                    desplegada. Decide una persona con capacidad de QA en el proyecto; quien entregó
                    no decide su propia entrega.
                </p>
                <p className="reviews__note">
                    Devolver la manda de nuevo a la cola con su prioridad intacta, y la razón que
                    escribas viaja en el paquete de contexto de quien la vuelva a tomar.
                </p>
                {total > loaded && (
                    <p className="reviews__note reviews__note--warn">
                        Se leyeron {loaded} de {total}. Filtrá por proyecto para ver el resto.
                    </p>
                )}
            </footer>
        </section>
    );
}

function Delivery({ card, focused, canReadEvents, isSameActor, names, repositoryInfo, tier, tierTo }) {
    const { task, round, commits, reservation, repository, returns, notes, attempt } = card;
    const repo = repositoryInfo(task.repository_id);
    const tokenKind = names.tokenKind(reservation?.tokenId);

    return (
        // El id es la ancla desde el tablero: `?tarea=` trae esta tarjeta a la
        // vista y la destaca sin filtrar lo demás.
        <article className={`delivery${focused ? ' delivery--focused' : ''}`} id={`entrega-${task.id}`}>
            <header className="delivery__header">
                <Link to={`/admin/tasks/${task.id}`} className="delivery__title">
                    <span className="delivery__id">#{task.id}</span> {task.title}
                </Link>

                <div className="delivery__tags">
                    {round && <span className="delivery__tag">Ronda {round.number}</span>}
                    {attempt > 1 && (
                        <span className="delivery__tag delivery__tag--warn">Intento {attempt}</span>
                    )}
                    <TierChip label={tier?.short} title={tier?.full} to={tierTo} />
                    {task.estimate && <span className="delivery__tag">Talla {task.estimate}</span>}
                </div>
            </header>

            {card.isLoading && <p className="delivery__who">Leyendo la entrega…</p>}

            {/* 1. WHO AND WHEN */}
            <p className="delivery__who">
                {round?.openedAt
                    ? <>Entregada el <strong>{formatDateTime(round.openedAt)}</strong></>
                    : 'Entregada'}
                {reservation?.tokenId != null && (
                    <> por <strong>{names.tokenName(reservation.tokenId)}</strong>
                        {tokenKind && <span className="delivery__kindtag">{tokenKind}</span>}
                    </>
                )}
                {reservation?.userId != null && (
                    <> · en nombre de {reservation.userName ?? names.userName(reservation.userId)}</>
                )}
                {round?.specRevision != null && <> · especificación rev. {round.specRevision}</>}
            </p>

            {card.roundsError && (
                <p className="delivery__blocked">
                    No se pudo leer la ronda de revisión: {card.roundsError.message}
                </p>
            )}

            {/* 2. THE VERSION, which is what an approval signs */}
            <div className="delivery__block">
                <h4 className="delivery__block-title">Versión entregada</h4>
                {round?.versioned
                    ? (
                        <p className="delivery__version">
                            <CommitLink hash={round.baseCommit} />
                            <span className="delivery__arrow" aria-label="hasta">→</span>
                            <CommitLink hash={round.deliveredCommit} strong />
                        </p>
                    )
                    : round && (
                        <p className="delivery__blocked">
                            Esta entrega <strong>no fijó versión</strong> (commit base y entregado):
                            se puede devolver, pero no aprobar.
                        </p>
                    )}
                <p className="delivery__repo">
                    {repo && <KindBadge kind={repo.kind} label={false} />}
                    <span>{repository?.name ?? repo?.name ?? `Repositorio #${task.repository_id}`}</span>
                    {repository?.main_branch && (
                        <span className="delivery__branch" title="Rama principal del repositorio. Core no registra la rama de trabajo de una entrega.">
                            rama principal: <code>{repository.main_branch}</code>
                        </span>
                    )}
                </p>
            </div>

            {/* 3. THE COMMITS */}
            <div className="delivery__block">
                <h4 className="delivery__block-title">
                    Commits ({commits.length})
                </h4>
                {commits.length === 0
                    ? <p className="delivery__muted">La entrega no registró ningún commit.</p>
                    : (
                        <ul className="delivery__hashes">
                            {commits.map(commit => (
                                <li key={commit.hash}>
                                    <CommitLink hash={commit.hash} />
                                    <span className="delivery__hash-message">{commit.message ?? 'Sin mensaje'}</span>
                                    {(commit.author || commit.committed_at) && (
                                        <span className="delivery__stamp">
                                            {[commit.author, commit.committed_at && formatDateTime(commit.committed_at)]
                                                .filter(Boolean).join(' · ')}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
            </div>

            {/* 4. THE EVALUATION, per criterion as it was delivered */}
            {round && <Evaluation round={round} fallbackCriteria={task.acceptance_criteria} />}

            {/* 5. THE LOG and the development report */}
            {round?.entries?.length > 0 && (
                <div className="delivery__block">
                    <h4 className="delivery__block-title">Bitácora de la ronda</h4>
                    <ul className="delivery__log">
                        {round.entries.map(entry => (
                            <li key={entry.id} className={`delivery__entry delivery__entry--${entry.kind}`}>
                                <span className="delivery__stamp">
                                    <span className="delivery__entry-kind">{entry.kind === 'ai' ? 'IA' : 'Persona'}</span>
                                    {' '}{entry.tokenId != null ? names.tokenName(entry.tokenId) : (entry.authorName ?? names.userName(entry.authorId))}
                                    {entry.at && ` · ${formatDateTime(entry.at)}`}
                                </span>
                                <div className="md md--field">{renderMarkdown(entry.body)}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="delivery__block">
                <h4 className="delivery__block-title">Informe de trabajo</h4>
                {!canReadEvents
                    ? <p className="delivery__muted">Hace falta <code>events:read</code> para leer lo que el agente reportó mientras trabajaba.</p>
                    : notes.length === 0
                        ? <p className="delivery__muted">El agente no dejó notas de avance.</p>
                        : (
                            <ul className="delivery__notes">
                                {notes.map(entry => (
                                    <li key={entry.id}>
                                        <span className="delivery__stamp">{formatDateTime(entry.at)}</span>
                                        <div className="md md--field">{renderMarkdown(entry.note)}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
            </div>

            {task.scope?.length > 0 && (
                <details className="delivery__block delivery__fold">
                    <summary className="delivery__block-title">Ámbito ({task.scope.length})</summary>
                    <ul className="delivery__scope">
                        {task.scope.map((path, index) => <li key={index}>{path}</li>)}
                    </ul>
                </details>
            )}

            {/* 6. PREVIOUS RETURNS */}
            {returns.length > 0 && (
                <div className="delivery__block">
                    <h4 className="delivery__block-title">Devoluciones previas ({returns.length})</h4>
                    <ul className="delivery__returns">
                        {returns.map(entry => (
                            <li key={entry.id}>
                                <span className="delivery__stamp">
                                    {formatDateTime(entry.at)} · {entry.userName ?? names.userName(entry.userId) ?? 'sin autor'}
                                </span>
                                <p className="delivery__reason">{entry.reason || 'Sin razón registrada'}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {isSameActor && (
                <p className="delivery__blocked">
                    Esta entrega salió de esta misma sesión, así que no la podés decidir desde acá:
                    tiene que mirarla otro actor. No es que falte el permiso — es la compuerta.
                </p>
            )}

            {!isSameActor && (
                <Gate domain="tasks:review">
                    <Verdict taskId={task.id} title={task.title} round={round} />
                </Gate>
            )}
        </article>
    );
}

function CommitLink({ hash, strong = false }) {
    if (!hash) return <span className="delivery__muted">—</span>;
    return (
        <Link
            to={`/admin/tasks/trace?hash=${hash}`}
            className={`delivery__hash${strong ? ' delivery__hash--strong' : ''}`}
            title={`${hash} — ver de dónde salió`}
        >
            {String(hash).slice(0, 10)}
        </Link>
    );
}

function Evaluation({ round, fallbackCriteria }) {
    const summary = round.summary;

    return (
        <div className="delivery__block">
            <h4 className="delivery__block-title">Evaluación por criterio</h4>

            {summary && (
                <p className="delivery__summary">
                    <span className="delivery__result delivery__result--cumple">{summary.cumple ?? 0} cumple</span>
                    <span className="delivery__result delivery__result--incumple">{summary.incumple ?? 0} incumple</span>
                    <span className="delivery__result delivery__result--no_comprobado">{summary.no_comprobado ?? 0} no comprobado</span>
                    {summary.sin_evaluar != null && (
                        <span className="delivery__result">{summary.sin_evaluar} sin evaluar</span>
                    )}
                </p>
            )}

            {round.criteria
                ? (
                    <ol className="delivery__criteria-list">
                        {round.criteria.map(criterion => (
                            <li key={criterion.index} className="delivery__criterion">
                                <span className="delivery__criterion-text">{criterion.text}</span>
                                {criterion.finding
                                    ? (
                                        <span className={`delivery__result delivery__result--${criterion.finding.result}`}>
                                            {FINDING_LABEL[criterion.finding.result] ?? criterion.finding.result}
                                        </span>
                                    )
                                    : <span className="delivery__result">Sin evaluar</span>}
                                {criterion.finding?.reason && (
                                    <p className="delivery__finding-reason">{criterion.finding.reason}</p>
                                )}
                                {criterion.finding?.evidence && (
                                    <p className="delivery__finding-evidence">Evidencia: {criterion.finding.evidence}</p>
                                )}
                            </li>
                        ))}
                    </ol>
                )
                : (
                    <>
                        <p className="delivery__blocked">
                            La ronda no fijó sus criterios (es anterior a esa instantánea). Se muestran
                            los de hoy como referencia; esta ronda no se puede aprobar.
                        </p>
                        <ul className="delivery__criteria">
                            {(fallbackCriteria ?? []).map((criterion, index) => <li key={index}>{criterion}</li>)}
                        </ul>
                    </>
                )}
        </div>
    );
}

// Approve or return THE ROUND ON THE CARD. Approving asks for a confirmation
// because it cannot be undone; returning asks for the reason Core requires.
function Verdict({ taskId, title, round }) {
    const [mode, setMode] = useState(null);
    const [reason, setReason] = useState('');
    const decide = useDecideRound(taskId);

    const blocker = approvalBlocker(round);
    const canReturn = !!round && round.state === 'open';

    const close = () => {
        setMode(null);
        setReason('');
        decide.reset();
    };

    const send = async (payload, done) => {
        try {
            await decide.mutateAsync({ review_round_id: round.id, ...payload });
            toast.success(done);
            close();
        } catch (err) {
            // A stale round is not an error to retry: somebody delivered again.
            if (err.status === 409 && err.gate === 'ronda') {
                toast.error('La tarea se volvió a entregar: la ronda cambió. Se recargó la tarjeta.');
            }
            // Everything else is rendered under the buttons, next to the decision.
        }
    };

    const onApprove = () => send({ result: 'approved', reason: null }, `Aprobada: ${title}`);
    const onReturn = () => send({ result: 'returned', reason: reason.trim() }, 'Devuelta a la cola');

    return (
        <div className="verdict">
            {mode === null && (
                <>
                    <div className="verdict__actions">
                        <button
                            type="button"
                            className="verdict__button verdict__button--accept"
                            onClick={() => setMode('approve')}
                            disabled={!!blocker}
                            title={blocker ?? undefined}
                        >
                            Aprobar
                        </button>
                        <button
                            type="button"
                            className="verdict__button"
                            onClick={() => setMode('return')}
                            disabled={!canReturn}
                        >
                            Devolver
                        </button>
                    </div>
                    {blocker && <p className="verdict__hint">{blocker}</p>}
                </>
            )}

            {mode === 'approve' && (
                <div className="verdict__confirm">
                    <p className="verdict__ask">
                        Vas a aprobar la <strong>ronda {round.number}</strong>, versión{' '}
                        <code>{String(round.deliveredCommit).slice(0, 10)}</code>. La tarea queda{' '}
                        <strong>hecha</strong> y no hay ruta que la traiga de vuelta.
                    </p>
                    <div className="verdict__actions">
                        <button
                            type="button"
                            className="verdict__button verdict__button--accept"
                            onClick={onApprove}
                            disabled={decide.isPending}
                        >
                            {decide.isPending ? 'Aprobando…' : 'Sí, aprobar'}
                        </button>
                        <button type="button" className="verdict__button" onClick={close}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {mode === 'return' && (
                <div className="verdict__confirm">
                    <label className="verdict__label" htmlFor={`reason-${taskId}`}>
                        Por qué se devuelve
                    </label>
                    <textarea
                        id={`reason-${taskId}`}
                        className="verdict__reason"
                        rows={3}
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Qué falta o qué está mal. Esto es lo que va a leer quien la retome."
                    />
                    <div className="verdict__actions">
                        <button
                            type="button"
                            className="verdict__button verdict__button--return"
                            onClick={onReturn}
                            disabled={decide.isPending || !reason.trim()}
                        >
                            {decide.isPending ? 'Devolviendo…' : 'Devolver con esta razón'}
                        </button>
                        <button type="button" className="verdict__button" onClick={close}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {decide.error && (
                <p className="verdict__error">
                    {decide.error.message || 'No se pudo registrar la decisión'}
                </p>
            )}
        </div>
    );
}
